// OverviewTab.js — Tab 4: Overview dashboard pulling metrics from all upstream tabs.
// Compares yields across DSV Senior, RoyETH, eigenETH, wstETH (plain), and Vanilla Loop.

import { useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { useSession, useSessionDispatch } from './SessionContext';
import { DEFAULT_STRATEGIES } from '../../lib/dsv-engine';
import { simulate } from '../../lib/dsv-engine';
import { getNavData, royethRateData } from '../../lib/nav-data-loader';
import { simulateRoyETH } from '../../lib/royeth-engine';
import { analyze, backtest } from '../../lib/yield-engine';
import COLORS from '../../lib/colors';

// ── Name mapping: DSV strategy name -> Tab 1 market name (same as other tabs) ──

const DSV_TO_MARKET = {
  'Fasanara mf-ONE': 'Fasanara mf-ONE',
  'Apollo ACRED (Securitize)': 'Apollo ACRED (Securitize)',
  'Pareto FalconX': 'Pareto FalconX',
  'Re Protocol reUSD': 'Re Protocol reUSD',
};

// ── Formatting helpers ──

const fmtPct = (v, d = 2) => (v * 100).toFixed(d) + '%';
const fmtPctRaw = (v, d = 2) => v.toFixed(d) + '%';

// ── Inline UI components (consistent with other tabs) ──

const Card = ({ title, children }) => (
  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4, padding: 16, marginBottom: 12 }}>
    {title && <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: 12 }}>{title}</div>}
    {children}
  </div>
);

const MetricRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{label}</span>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: color || COLORS.ink }}>{value}</span>
  </div>
);

const Slider = ({ label, value, onChange, min, max, step, suffix = '' }) => {
  const decimals = step < 0.01 ? 3 : step < 0.1 ? 2 : step < 1 ? 1 : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>{label}</span>
        <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: COLORS.ink }}>
          {typeof value === 'number' ? value.toFixed(decimals) : value}{suffix}
        </span>
      </div>
      <input type="range" className="integrated-slider" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', height: 4, appearance: 'none', WebkitAppearance: 'none', background: `linear-gradient(to right, ${COLORS.accent} 0%, ${COLORS.accent} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} ${((value - min) / (max - min)) * 100}%, ${COLORS.borderDark} 100%)`, borderRadius: 2, outline: 'none', cursor: 'pointer' }} />
    </div>
  );
};

const Toggle = ({ label, value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}
  >
    <div style={{
      width: 36, height: 20, borderRadius: 10,
      background: value ? COLORS.accent : COLORS.borderDark,
      border: `1px solid ${value ? COLORS.accent : COLORS.borderDark}`,
      position: 'relative', transition: 'all 0.2s', flexShrink: 0,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 8,
        background: value ? COLORS.bg : COLORS.textTertiary,
        position: 'absolute', top: 1, left: value ? 18 : 1,
        transition: 'left 0.2s',
      }} />
    </div>
    {label && <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{label}</span>}
  </div>
);

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: COLORS.ink, color: COLORS.bg, padding: '8px 12px', borderRadius: 2, fontFamily: "'Fragment Mono', monospace", fontSize: 11, lineHeight: 1.6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.payload.color || COLORS.bg }}>{p.payload.label}: {typeof p.value === 'number' ? p.value.toFixed(2) + '%' : p.value}</div>
      ))}
    </div>
  );
};

// ── Status dot for pipeline banner ──

const StatusDot = ({ status }) => {
  const color = status === 'complete' ? COLORS.positive : status === 'partial' ? COLORS.caution : COLORS.textTertiary;
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6, verticalAlign: 'middle' }} />;
};

// ── Health factor color helper ──

const hfColor = (hf) => hf > 1.5 ? COLORS.positive : hf > 1.15 ? COLORS.caution : COLORS.negative;

// ── Main component ──

export default function OverviewTab() {
  const { state } = useSession();
  const dispatch = useSessionDispatch();

  // ── Dispatch helper for overview config ──
  const updateOverview = useCallback((patch) => {
    dispatch({ type: 'UPDATE_OVERVIEW', patch });
  }, [dispatch]);

  // ── Pipeline status ──
  const marketNames = Object.keys(state.markets);
  const marketsComplete = marketNames.filter(n => state.markets[n].status === 'complete').length;
  const marketsIncomplete = marketNames.length - marketsComplete;

  // ── Compute DSV result (replicate from RoyethTab) ──
  const dsvResult = useMemo(() => {
    const strategies = DEFAULT_STRATEGIES.map(defaultStrat => {
      const alloc = state.dsv.allocations[defaultStrat.name];
      if (!alloc) return { ...defaultStrat, enabled: false };

      const base = { ...defaultStrat, enabled: alloc.enabled, allocation: alloc.allocation };

      if (!alloc.overrideTab1 && defaultStrat.minCoverage > 0) {
        const marketName = DSV_TO_MARKET[defaultStrat.name];
        const market = marketName ? state.markets[marketName] : null;
        if (market) {
          base.minCoverage = market.params.minCoverage;
          base.recoveryDuration = market.params.recoveryDuration;
          base.seniorFee = market.params.stFee;
          base.juniorFee = market.params.jtFee;
          base.yieldShareFee = market.params.ysFee;
          base.finalizedLossThreshold = market.liquidation.finalizedLossThreshold;
          base.selfLiquidationBonus = market.liquidation.selfLiquidationBonus;
        }
      }

      return base;
    });

    const navData = {};
    for (const strat of strategies) {
      if (!strat.enabled) continue;
      const nd = getNavData(strat.name);
      if (nd) {
        navData[strat.name] = { dates: nd.dates, navs: nd.navs };
      }
    }

    const simParams = state.dsv.simParams;
    return simulate({
      strategies,
      navData: Object.keys(navData).length > 0 ? navData : null,
      simParams: {
        reversionDays: simParams.reversionDays,
        liquidationBonusMode: simParams.liquidationBonusMode,
        liquidationRedemptionRate: simParams.liquidationRedemptionRate,
      },
    });
  }, [state.markets, state.dsv]);

  // ── Compute RoyETH result ──
  const royethResult = useMemo(() => {
    if (!dsvResult?.dates?.length || dsvResult.dates.length < 2) return null;
    return simulateRoyETH({
      dsvResult,
      rateData: royethRateData,
      config: {
        aaveLTV: state.royeth.aaveLTV * 100,
        liqThreshold: state.royeth.liqThreshold * 100,
        useHistoricRates: state.royeth.useHistoricRates,
        stakingYieldOverride: state.royeth.stakingYield * 100,
        aaveSupplyOverride: state.royeth.aaveSupply * 100,
        usdcBorrowOverride: state.royeth.usdcBorrow * 100,
        ltvMode: state.royeth.ltvMode,
        maxDsvRedemption: state.royeth.maxDsvRedemption,
      },
    });
  }, [dsvResult, state.royeth]);

  const hasDsvData = dsvResult?.dates?.length >= 2;
  const hasRoyethData = royethResult != null;

  // ── Derive DSV annualized return as a percentage for yield-engine ──
  const dsvAnnualizedPct = useMemo(() => {
    if (!hasDsvData) return 7; // fallback default
    return dsvResult.aggregate.summary.annualizedReturn * 100;
  }, [hasDsvData, dsvResult]);

  // ── Build yield-engine config from upstream state ──
  const yieldEngineConfig = useMemo(() => ({
    srYield: dsvAnnualizedPct,
    stakingYield: state.royeth.stakingYield * 100,
    aaveSupplyYield: state.royeth.aaveSupply * 100,
    usdcBorrowRate: state.royeth.usdcBorrow * 100,
    aaveLTV: state.royeth.aaveLTV * 100,
    aaveLiqThreshold: state.royeth.liqThreshold * 100,
    morphoLTV: state.overview.eigenETH.morphoLTV * 100,
    ethBorrowPremium: state.overview.eigenETH.ethBorrowPremium * 100,
    morphoLiqThreshold: state.overview.eigenETH.liqThreshold * 100,
    modelDepeg: state.overview.eigenETH.depeg,
    depegPct: state.overview.eigenETH.depegPct * 100,
    baseEthBorrowRate: state.overview.vanilla.baseEthBorrow * 100,
    vanillaSupplyYield: state.overview.vanilla.supplyYield * 100,
    mirrorAaveSupply: false,
  }), [dsvAnnualizedPct, state.royeth, state.overview]);

  // ── Run analyze() ──
  const analyzeResult = useMemo(() => {
    return analyze(yieldEngineConfig);
  }, [yieldEngineConfig]);

  // ── Run backtest() ──
  const backtestResult = useMemo(() => {
    return backtest(yieldEngineConfig);
  }, [yieldEngineConfig]);

  // ── Yield waterfall data ──
  const waterfallData = useMemo(() => {
    const dsvYield = hasDsvData ? dsvResult.aggregate.summary.annualizedReturn * 100 : 0;
    const royethYield = hasRoyethData ? royethResult.summary.annualizedReturn * 100 : 0;
    const eigenYield = analyzeResult.yields.eigenETH * 100;
    const wstethYield = analyzeResult.yields.oneXwstETH * 100;
    const vanillaYield = analyzeResult.yields.vanilla * 100;

    return [
      { name: 'DSV Senior', yield: dsvYield, color: COLORS.positive, label: 'DSV Senior' },
      { name: 'RoyETH', yield: royethYield, color: COLORS.accent, label: 'RoyETH' },
      { name: 'eigenETH', yield: eigenYield, color: COLORS.ethBlue, label: 'eigenETH' },
      { name: 'wstETH', yield: wstethYield, color: COLORS.lidoBlue, label: 'wstETH (1x)' },
      { name: 'Vanilla Loop', yield: vanillaYield, color: COLORS.accentAlt, label: 'Vanilla Loop' },
    ];
  }, [hasDsvData, dsvResult, hasRoyethData, royethResult, analyzeResult]);

  // ── Backtest summary ──
  const backtestSummary = useMemo(() => {
    const totalEvents = backtestResult.eventResults.length;
    const passCount = backtestResult.eventResults.filter(e => e.survives).length;
    const failCount = totalEvents - passCount;

    // Find worst failing event
    const failures = backtestResult.eventResults.filter(e => !e.survives);
    const worstEvent = failures.length > 0
      ? failures.reduce((a, b) => b.drawdown > a.drawdown ? b : a)
      : null;

    // Max survivable window
    let maxSurvivableWindow = 0;
    for (const wr of backtestResult.windowResults) {
      if (wr.allPass) maxSurvivableWindow = wr.window;
    }

    return { totalEvents, passCount, failCount, worstEvent, maxSurvivableWindow };
  }, [backtestResult]);

  // ── Render ──
  return (
    <div style={{ padding: '20px 24px' }}>

      {/* ── Pipeline Summary Banner ── */}
      <Card title="Pipeline Status">
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {/* Markets */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <StatusDot status={marketsComplete === marketNames.length && marketNames.length > 0 ? 'complete' : marketsComplete > 0 ? 'partial' : 'incomplete'} />
            <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 12, color: COLORS.ink }}>
              Markets: {marketNames.length} configured ({marketsComplete} complete{marketsIncomplete > 0 ? `, ${marketsIncomplete} incomplete` : ''})
            </span>
          </div>
          {/* DSV Portfolio */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <StatusDot status={state.dsv.status} />
            <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 12, color: COLORS.ink }}>
              DSV Portfolio: {state.dsv.status}
              {hasDsvData && <span style={{ color: COLORS.positive, marginLeft: 6 }}>{fmtPct(dsvResult.aggregate.summary.annualizedReturn)} ann.</span>}
            </span>
          </div>
          {/* RoyETH */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <StatusDot status={state.royeth.status} />
            <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 12, color: COLORS.ink }}>
              RoyETH: {state.royeth.status}
              {hasRoyethData && <span style={{ color: COLORS.accent, marginLeft: 6 }}>{fmtPct(royethResult.summary.annualizedReturn)} ann.</span>}
            </span>
          </div>
        </div>
      </Card>

      {/* ── Two-column layout ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── Left Panel: Config ── */}
        <div>
          {/* eigenETH Config */}
          <Card title="eigenETH Config">
            <Slider
              label="Morpho LTV"
              value={parseFloat((state.overview.eigenETH.morphoLTV * 100).toFixed(0))}
              onChange={(v) => updateOverview({ eigenETH: { morphoLTV: v / 100 } })}
              min={0} max={90} step={1} suffix="%"
            />
            <Slider
              label="ETH Borrow Premium"
              value={parseFloat((state.overview.eigenETH.ethBorrowPremium * 100).toFixed(1))}
              onChange={(v) => updateOverview({ eigenETH: { ethBorrowPremium: v / 100 } })}
              min={0} max={5} step={0.1} suffix="%"
            />
            <Slider
              label="Liquidation Threshold"
              value={parseFloat((state.overview.eigenETH.liqThreshold * 100).toFixed(0))}
              onChange={(v) => updateOverview({ eigenETH: { liqThreshold: v / 100 } })}
              min={70} max={95} step={1} suffix="%"
            />
            <Toggle
              label="Model Depeg Risk"
              value={state.overview.eigenETH.depeg}
              onChange={(v) => updateOverview({ eigenETH: { depeg: v } })}
            />
            {state.overview.eigenETH.depeg && (
              <Slider
                label="Depeg %"
                value={parseFloat((state.overview.eigenETH.depegPct * 100).toFixed(0))}
                onChange={(v) => updateOverview({ eigenETH: { depegPct: v / 100 } })}
                min={0} max={25} step={1} suffix="%"
              />
            )}
          </Card>

          {/* Vanilla wstETH/ETH Loop Config */}
          <Card title="Vanilla wstETH/ETH Loop Config">
            <Slider
              label="Base ETH Borrow Rate"
              value={parseFloat((state.overview.vanilla.baseEthBorrow * 100).toFixed(1))}
              onChange={(v) => updateOverview({ vanilla: { baseEthBorrow: v / 100 } })}
              min={0} max={10} step={0.1} suffix="%"
            />
            <Slider
              label="Supply Yield"
              value={parseFloat((state.overview.vanilla.supplyYield * 100).toFixed(2))}
              onChange={(v) => updateOverview({ vanilla: { supplyYield: v / 100 } })}
              min={0} max={1} step={0.01} suffix="%"
            />
          </Card>

          {/* Upstream input summary */}
          <Card title="Upstream Inputs">
            <MetricRow label="DSV Annualized" value={hasDsvData ? fmtPct(dsvResult.aggregate.summary.annualizedReturn) : 'N/A'} color={COLORS.positive} />
            <MetricRow label="Staking Yield" value={fmtPct(state.royeth.stakingYield)} />
            <MetricRow label="Aave Supply" value={fmtPct(state.royeth.aaveSupply)} />
            <MetricRow label="USDC Borrow" value={fmtPct(state.royeth.usdcBorrow)} />
            <MetricRow label="Aave LTV" value={fmtPct(state.royeth.aaveLTV)} />
            <MetricRow label="Aave Liq. Threshold" value={fmtPct(state.royeth.liqThreshold)} />
          </Card>
        </div>

        {/* ── Right Panel: Charts and Metrics ── */}
        <div>
          {/* 1. Yield Waterfall */}
          <Card title="Yield Comparison (Annualized %)">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={waterfallData} margin={{ top: 10, right: 30, left: 10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                />
                <YAxis
                  tickFormatter={(v) => v.toFixed(1) + '%'}
                  tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                  width={60}
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="yield" name="Yield" radius={[4, 4, 0, 0]}>
                  {waterfallData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* 2. Risk Dashboard */}
          <Card title="Risk Dashboard">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Health Factors */}
              <div>
                <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>
                  Health Factors
                </div>
                <MetricRow
                  label="Aave HF"
                  value={analyzeResult.liquidation.aave.healthFactor === Infinity ? '\u221E' : analyzeResult.liquidation.aave.healthFactor.toFixed(2)}
                  color={hfColor(analyzeResult.liquidation.aave.healthFactor)}
                />
                <MetricRow
                  label="Morpho HF"
                  value={analyzeResult.liquidation.morpho.healthFactor === Infinity ? '\u221E' : analyzeResult.liquidation.morpho.healthFactor.toFixed(2)}
                  color={hfColor(analyzeResult.liquidation.morpho.healthFactor)}
                />
                {state.overview.eigenETH.depeg && (
                  <MetricRow
                    label="Morpho HF (post-depeg)"
                    value={analyzeResult.liquidation.morpho.hfAfterDepeg === Infinity ? '\u221E' : analyzeResult.liquidation.morpho.hfAfterDepeg.toFixed(2)}
                    color={hfColor(analyzeResult.liquidation.morpho.hfAfterDepeg)}
                  />
                )}
              </div>

              {/* Max Safe LTV */}
              <div>
                <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>
                  Max Safe LTV
                </div>
                <MetricRow
                  label="Aave Max Safe LTV"
                  value={fmtPctRaw(analyzeResult.safeLTV.aave.max)}
                  color={analyzeResult.safeLTV.aave.headroom > 0 ? COLORS.positive : COLORS.negative}
                />
                <MetricRow
                  label="Morpho Max Safe LTV"
                  value={fmtPctRaw(analyzeResult.safeLTV.morpho.max)}
                  color={analyzeResult.safeLTV.morpho.headroom > 0 ? COLORS.positive : COLORS.negative}
                />
              </div>
            </div>

            {/* Liquidation Headroom */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>
                Liquidation Headroom
              </div>
              <MetricRow
                label="Aave Headroom"
                value={fmtPctRaw(analyzeResult.safeLTV.aave.headroom) + ' LTV'}
                color={analyzeResult.safeLTV.aave.headroom > 0 ? COLORS.positive : COLORS.negative}
              />
              <MetricRow
                label="Aave Max ETH Drop"
                value={fmtPct(analyzeResult.liquidation.aave.maxDrop)}
                color={analyzeResult.liquidation.aave.maxDrop > 0.3 ? COLORS.positive : analyzeResult.liquidation.aave.maxDrop > 0.15 ? COLORS.caution : COLORS.negative}
              />
              <MetricRow
                label="Morpho Headroom"
                value={fmtPctRaw(analyzeResult.safeLTV.morpho.headroom) + ' LTV'}
                color={analyzeResult.safeLTV.morpho.headroom > 0 ? COLORS.positive : COLORS.negative}
              />
              <MetricRow
                label="Morpho Max Depeg"
                value={fmtPct(analyzeResult.liquidation.morpho.maxDepeg)}
                color={analyzeResult.liquidation.morpho.maxDepeg > 0.1 ? COLORS.positive : analyzeResult.liquidation.morpho.maxDepeg > 0.05 ? COLORS.caution : COLORS.negative}
              />
            </div>
          </Card>

          {/* 3. Constraint Checks */}
          <Card title={`Constraint Checks (${analyzeResult.constraintsSummary.passingCount}/${analyzeResult.constraintsSummary.totalCount} passing)`}>
            {Object.entries(analyzeResult.constraints).map(([key, c]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                <span style={{
                  display: 'inline-block', width: 18, height: 18, lineHeight: '18px', textAlign: 'center',
                  borderRadius: 2, fontSize: 11, fontWeight: 700,
                  background: c.pass ? COLORS.positiveBg : COLORS.negativeBg,
                  color: c.pass ? COLORS.positive : COLORS.negative,
                  border: `1px solid ${c.pass ? COLORS.positiveBorder : COLORS.negativeBorder}`,
                }}>
                  {c.pass ? '\u2713' : '\u2717'}
                </span>
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: c.pass ? COLORS.textBody : COLORS.negative }}>
                  {c.description}
                </span>
              </div>
            ))}
          </Card>

          {/* 4. Historical Backtest Summary */}
          <Card title="Historical Backtest Summary">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <MetricRow label="Total Events Tested" value={backtestSummary.totalEvents} />
                <MetricRow
                  label="Pass"
                  value={backtestSummary.passCount}
                  color={COLORS.positive}
                />
                <MetricRow
                  label="Fail"
                  value={backtestSummary.failCount}
                  color={backtestSummary.failCount > 0 ? COLORS.negative : COLORS.positive}
                />
                <MetricRow
                  label="Max Survivable Window"
                  value={backtestSummary.maxSurvivableWindow > 0 ? `${backtestSummary.maxSurvivableWindow}d` : 'None'}
                  color={backtestSummary.maxSurvivableWindow >= 7 ? COLORS.positive : backtestSummary.maxSurvivableWindow >= 3 ? COLORS.caution : COLORS.negative}
                />
                <MetricRow
                  label="Morpho Survives Depeg"
                  value={backtestResult.morphoSurvives ? 'Yes' : 'No'}
                  color={backtestResult.morphoSurvives ? COLORS.positive : COLORS.negative}
                />
              </div>
              <div>
                {backtestSummary.worstEvent && (
                  <>
                    <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>
                      Worst Failing Event
                    </div>
                    <MetricRow label="Date" value={backtestSummary.worstEvent.date} />
                    <MetricRow label="Drawdown" value={backtestSummary.worstEvent.drawdown.toFixed(1) + '%'} color={COLORS.negative} />
                    <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary, marginTop: 4, lineHeight: 1.5 }}>
                      {backtestSummary.worstEvent.event}
                    </div>
                  </>
                )}
                {!backtestSummary.worstEvent && (
                  <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 12, color: COLORS.positive, padding: '16px 0' }}>
                    All historical events pass.
                  </div>
                )}
              </div>
            </div>

            {/* Per-window summary table */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>
                Results by Window
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Window', 'Events', 'Pass', 'Fail', 'Worst Drop', 'Status'].map(h => (
                      <th key={h} style={{
                        fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: COLORS.textTertiary, padding: '6px 8px',
                        borderBottom: `2px solid ${COLORS.borderLight}`, textAlign: 'left', fontWeight: 400,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {backtestResult.windowResults.map(wr => (
                    <tr key={wr.window} style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
                      <td style={{ padding: '6px 8px', fontFamily: "'Fragment Mono', monospace", fontSize: 11 }}>{wr.window}d</td>
                      <td style={{ padding: '6px 8px', fontFamily: "'Fragment Mono', monospace", fontSize: 11 }}>{wr.totalEvents}</td>
                      <td style={{ padding: '6px 8px', fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.positive }}>{wr.passCount}</td>
                      <td style={{ padding: '6px 8px', fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: wr.failCount > 0 ? COLORS.negative : COLORS.textTertiary }}>{wr.failCount}</td>
                      <td style={{ padding: '6px 8px', fontFamily: "'Fragment Mono', monospace", fontSize: 11 }}>{wr.worstDrop.toFixed(1)}%</td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{
                          display: 'inline-block', fontFamily: "'Fragment Mono', monospace", fontSize: 9,
                          fontWeight: 700, letterSpacing: '0.08em', padding: '2px 6px', borderRadius: 2,
                          background: wr.allPass ? COLORS.positiveBg : COLORS.negativeBg,
                          color: wr.allPass ? COLORS.positive : COLORS.negative,
                          border: `1px solid ${wr.allPass ? COLORS.positiveBorder : COLORS.negativeBorder}`,
                        }}>
                          {wr.allPass ? 'PASS' : 'FAIL'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 5. Yield Breakdown Detail */}
          <Card title="Yield Breakdown">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>
                  eigenETH (Leveraged)
                </div>
                <MetricRow label="Leverage" value={analyzeResult.leverage.toFixed(2) + 'x'} />
                <MetricRow label="Gross Yield" value={fmtPct(analyzeResult.yields.eigenETHComponents.gross)} color={COLORS.positive} />
                <MetricRow label="Borrow Cost" value={fmtPct(analyzeResult.yields.eigenETHComponents.borrowCost)} color={COLORS.negative} />
                <MetricRow label="Net eigenETH" value={fmtPct(analyzeResult.yields.eigenETH)} color={COLORS.ethBlue} />
              </div>
              <div>
                <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>
                  Vanilla Loop (same leverage)
                </div>
                <MetricRow label="Gross Yield" value={fmtPct(analyzeResult.yields.vanillaComponents.gross)} color={COLORS.positive} />
                <MetricRow label="Borrow Cost" value={fmtPct(analyzeResult.yields.vanillaComponents.borrowCost)} color={COLORS.negative} />
                <MetricRow label="Net Vanilla" value={fmtPct(analyzeResult.yields.vanilla)} color={COLORS.accentAlt} />
                <MetricRow
                  label="eigenETH vs Vanilla"
                  value={fmtPct(analyzeResult.comparisons.vsVanilla)}
                  color={analyzeResult.comparisons.vsVanilla >= 0 ? COLORS.positive : COLORS.negative}
                />
                <MetricRow
                  label="eigenETH vs 1x wstETH"
                  value={fmtPct(analyzeResult.comparisons.vs1x)}
                  color={analyzeResult.comparisons.vs1x >= 0 ? COLORS.positive : COLORS.negative}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
